import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Waves from "@/components/Waves";

import shopifyLogo from "@/assets/partners/shopify.svg";
import shopifyIcon from "@/assets/partners/shopify-icon.svg";
import webflowLogo from "@/assets/partners/webflow.svg";
import webflowIcon from "@/assets/partners/webflow-icon.svg";
import wordpressLogo from "@/assets/partners/wordpress.svg";
import woocommerceLogo from "@/assets/partners/woocommerce.svg";
import notionLogo from "@/assets/partners/notion.svg";
import lovableLogo from "@/assets/partners/lovable.svg";
import lovableIcon from "@/assets/partners/lovable-icon.svg";
import openaiLogo from "@/assets/partners/openai.svg";
import hubspotLogo from "@/assets/partners/hubspot.svg";
import zapierLogo from "@/assets/partners/zoho.svg";

const PLATFORMS = [
  { name: "Shopify", logo: shopifyLogo, link: "/shopify", height: "h-5 sm:h-6 lg:h-7" },
  { name: "Webflow", logo: webflowLogo, link: "/webflow", height: "h-[1.125rem] sm:h-5 lg:h-6" },
  { name: "WordPress", logo: wordpressLogo, link: "/wordpress", height: "h-5 sm:h-6 lg:h-7" },
  { name: "WooCommerce", logo: woocommerceLogo, link: "/woocommerce", height: "h-4 sm:h-[1.125rem] lg:h-5" },
  { name: "Notion", logo: notionLogo, link: "/solutions/notion", height: "h-5 sm:h-6 lg:h-7" },
  { name: "Lovable", logo: lovableLogo, link: "/lovable", height: "h-[1.125rem] sm:h-5 lg:h-6" },
];

const SERVICE_PILLS = [
  { label: "ECOMMERCE", desc: "Shopify stores & growth", href: "/ecommerce", logos: [shopifyIcon, woocommerceLogo] },
  { label: "WEB", desc: "Modern websites & apps", href: "/web", logos: [webflowIcon, lovableIcon, wordpressLogo] },
  { label: "AI", desc: "Automation & workflows", href: "/ai", logos: [openaiLogo, hubspotLogo, zapierLogo], extra: 4 },
];

export function Hero() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background">
      {/* Waves background */}
      <div className="absolute inset-0 z-0 opacity-50 sm:opacity-100" style={{ maskImage: 'linear-gradient(to bottom, transparent 0%, transparent 80px, black 20%, black 28%, transparent 80%)', WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, transparent 80px, black 20%, black 28%, transparent 80%)' }}>
        <Waves
          lineColor="rgba(157, 128, 88, 0.6)"
          backgroundColor="transparent"
          waveSpeedX={0.005}
          waveSpeedY={0.04}
          waveAmpX={15}
          waveAmpY={5}
          friction={0.9}
          tension={0.01}
          maxCursorMove={120}
          xGap={12}
          yGap={36}
        />
      </div>


      <div className="container relative z-10 px-6 pt-32 pb-16 flex flex-col items-center">

        {/* Headline */}
        <motion.h1
          className="text-4xl sm:text-5xl lg:text-[4rem] xl:text-[4.5rem] font-display font-bold leading-[0.95] mb-4 text-foreground text-center max-w-5xl text-balance"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08 }}
        >
          {t("Leading")} <em className="not-italic italic">{t("Shopify, WordPress")}</em>
          {lang === "ar" ? " " : <br />}
          <span className="text-gradient">{t("& Webflow Agency in Kuwait")}</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          className="text-base sm:text-lg text-muted-foreground max-w-[29rem] mx-auto mb-12 leading-relaxed text-center text-balance"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.18 }}
        >
          {t(
            "Wavai is a Kuwait-based web and eCommerce agency building high-converting Shopify stores, premium WordPress sites and Webflow experiences for leading brands across Kuwait and the GCC."
          )}
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="flex items-center justify-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.28 }}
        >
          <Button variant="hero" size="xl" asChild>
            <Link to={localePath("/start-a-project")}>
              {t("Start a Project")}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </motion.div>


      </div>

      {/* Service Pills — full width, above logo strip */}
      <motion.div
        className="relative z-10 flex flex-col sm:flex-row items-stretch w-full border-t border-border/60"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.38 }}
      >
        {SERVICE_PILLS.map((pill, i) => (
          <Link
            key={pill.label}
            to={localePath(pill.href)}
            className={`flex-1 flex items-center justify-between py-6 px-6 transition-all duration-300 hover:bg-primary/[0.06] ${
              i < SERVICE_PILLS.length - 1 ? "border-b sm:border-b-0 sm:border-r border-border/60" : ""
            }`}
          >
            <div className="text-left">
              <span className="text-[17px] font-sans font-medium text-foreground block">
                {t(pill.label)}
              </span>
              <span className="text-xs text-muted-foreground mt-0.5 block">
                {t(pill.desc)}
              </span>
            </div>
            <div className="flex items-center -space-x-2 ml-4">
              {pill.logos.map((logo, j) => (
                <div
                  key={j}
                  className="w-7 h-7 rounded-full bg-background border-2 border-border/60 flex items-center justify-center shadow-sm"
                  style={{ zIndex: pill.logos.length - j }}
                >
                  <img src={logo} alt="" className="w-3.5 h-3.5 object-contain" />
                </div>
              ))}
              {pill.extra && (
                <div
                  className="w-7 h-7 rounded-full bg-muted border-2 border-border/60 flex items-center justify-center shadow-sm text-[10px] font-semibold text-muted-foreground"
                  style={{ zIndex: 0 }}
                >
                  +{pill.extra}
                </div>
              )}
            </div>
          </Link>
        ))}
      </motion.div>

      {/* Platform logos — expertise strip */}
      <div className="relative z-10 border-t border-border/60">
        <div className="container px-6 py-12 sm:py-14">
          <motion.div
            className="flex flex-wrap items-center justify-center gap-10 sm:gap-12 lg:gap-16"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {PLATFORMS.map((platform) => (
              <Link
                key={platform.name}
                to={localePath(platform.link)}
                className="group flex items-center justify-center"
              >
                <div className={`${platform.height} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
                  <img
                    src={platform.logo}
                    alt={platform.name}
                    className="h-full w-auto object-contain"
                  />
                </div>
              </Link>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
